import React, { useState, useEffect } from 'react';
import { User, LogOut, Search, Moon, Sun, UserCheck, X, Users, ShieldAlert, Lock, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { APP_VERSION } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { NotificationBell } from './notifications/NotificationBell';
import { LiveChatNotification } from './LiveChatNotification';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Role hierarchy for Walk As permissions
const ROLE_HIERARCHY = {
  super_admin: ['admin', 'itil_staff', 'end_user'], // Can walk as all lower roles
  admin: ['itil_staff', 'end_user'], // Can only walk as itil_staff and end_user
  itil_staff: [], // Cannot use Walk As
  end_user: [] // Cannot use Walk As
};

export const Topbar = () => {
  const { user, logout, canWalkAs, startWalkAs, isWalkingAs, walkAsUser, exitWalkAs, originalUser } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [walkAsDialogOpen, setWalkAsDialogOpen] = useState(false);
  const [restrictionDialogOpen, setRestrictionDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Get current actor's role (when walking as, use the impersonated user's role for restrictions)
  // This ensures admins can't bypass restrictions by starting as super_admin and walking as admin
  const actorRole = user?.role;
  const allowedRolesToWalkAs = ROLE_HIERARCHY[actorRole] || [];

  // Load users when dialog opens
  useEffect(() => {
    if (walkAsDialogOpen && canWalkAs) {
      loadUsers();
    }
  }, [walkAsDialogOpen, canWalkAs]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.getUsers();
      // Filter out the current user (original user if walking as)
      const currentUserId = originalUser?.id || user?.id;
      setUsers((response.data || []).filter(u => u.id !== currentUserId && u.active));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Check if current actor can walk as a specific user based on role hierarchy
  const canWalkAsUser = (targetUser) => {
    if (actorRole === 'super_admin') return true; // Super admin can walk as anyone
    return allowedRolesToWalkAs.includes(targetUser.role);
  };

  const handleWalkAs = (targetUser) => {
    // Check if actor has permission to walk as this user
    if (!canWalkAsUser(targetUser)) {
      // Show restriction dialog for admin trying to walk as super_admin
      setRestrictionDialogOpen(true);
      return;
    }
    
    startWalkAs(targetUser);
    setWalkAsDialogOpen(false);
    setSearchTerm('');
  };

  // Filter and sort users - show walkable users first, then restricted ones
  const filteredUsers = users
    .filter(u => 
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.role?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Sort walkable users first
      const aCanWalk = canWalkAsUser(a);
      const bCanWalk = canWalkAsUser(b);
      if (aCanWalk && !bCanWalk) return -1;
      if (!aCanWalk && bCanWalk) return 1;
      return 0;
    });

  // Function to trigger command palette
  const openCommandPalette = () => {
    // Dispatch Ctrl+K event to open command palette
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      ctrlKey: true,
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  return (
    <div className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50" data-testid="topbar">
      <div className="h-full px-6 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-muted-foreground">Welcome back,</h2>
          <p className="text-lg font-semibold" data-testid="user-name">{user?.full_name}</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Search/Command Button */}
          <Button 
            variant="outline" 
            className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground w-64 justify-start"
            onClick={openCommandPalette}
            data-testid="command-palette-trigger"
          >
            <Search className="h-4 w-4" />
            <span className="text-sm">Search...</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>
          
          {/* Mobile search button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="sm:hidden"
            onClick={openCommandPalette}
            data-testid="command-palette-trigger-mobile"
          >
            <Search className="h-5 w-5" />
          </Button>
          
          {/* Notification Bell */}
          <NotificationBell />
          
          {/* Live Chat Status */}
          <LiveChatNotification />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1 capitalize">Role: {user?.role?.replace('_', ' ')}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Walk As option - only for admin/super_admin */}
              {canWalkAs && (
                <>
                  <DropdownMenuItem onClick={() => setWalkAsDialogOpen(true)} data-testid="walk-as-trigger">
                    <Users className="mr-2 h-4 w-4" />
                    <span>Walk As User</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              
              <DropdownMenuItem onClick={() => window.location.href = '/profile'} data-testid="my-profile-link">
                <User className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                {isDark ? (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    <span>Light Mode</span>
                  </>
                ) : (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    <span>Dark Mode</span>
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.location.href = '/settings/notifications'} data-testid="notification-settings-link">
                <Settings className="mr-2 h-4 w-4" />
                <span>Notification Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <p className="text-xs text-muted-foreground">Version: <span className="font-mono" data-testid="app-version">{APP_VERSION}</span></p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Walk As Dialog */}
      <Dialog open={walkAsDialogOpen} onOpenChange={setWalkAsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Walk As User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a user to view the application as them. This helps debug and test user experiences.
            </p>
            <Input
              placeholder="Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="walk-as-search"
            />
            <ScrollArea className="h-[300px] border rounded-md">
              {loadingUsers ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No users found
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {/* Info banner for admins */}
                  {actorRole === 'admin' && (
                    <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <p className="text-xs text-amber-800 dark:text-amber-300">
                        <ShieldAlert className="h-3 w-3 inline mr-1" />
                        As an Admin, you can only walk as ITIL Staff and End Users.
                      </p>
                    </div>
                  )}
                  {filteredUsers.map((u) => {
                    const isRestricted = !canWalkAsUser(u);
                    return (
                      <button
                        key={u.id}
                        onClick={() => handleWalkAs(u)}
                        className={`w-full flex items-center gap-3 p-3 rounded-md text-left transition-colors ${
                          isRestricted 
                            ? 'opacity-60 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-not-allowed' 
                            : 'hover:bg-accent'
                        }`}
                        data-testid={`walk-as-user-${u.id}`}
                      >
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          isRestricted 
                            ? 'bg-red-100 dark:bg-red-900/30' 
                            : 'bg-primary/10'
                        }`}>
                          {isRestricted ? (
                            <Lock className="h-4 w-4 text-red-500" />
                          ) : (
                            <User className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{u.full_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                          isRestricted 
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                            : 'bg-muted'
                        }`}>
                          {u.role?.replace('_', ' ')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restriction Dialog - shown when admin tries to walk as super_admin */}
      <Dialog open={restrictionDialogOpen} onOpenChange={setRestrictionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" />
              Access Restricted
            </DialogTitle>
            <DialogDescription className="pt-4 text-base">
              You cannot walk as Super Admin users. Please reach out to a Super Admin if you need to make a change requiring Super Admin access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setRestrictionDialogOpen(false)} data-testid="restriction-dialog-close">
              I Understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};